import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import BaseController from '../../BaseController';
import { Inject } from '@odinjs/odin';
import { ApplicationRoute } from '../../../constants/routes';
import ClinicalNotesService from '../clinical-note-service';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';

import { ActionAPI, ModalProps, ModalService } from '@philips-internal/oswald-ui-components';
import { ModalType } from '@philips-internal/oswald-ui-constants';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { ClinicalNotesMenuAliases as Alias, FragmentNames } from '../../../codegen';
import SuepService from '../../../controllers/suep/suep-service';

@Controller({ component: Alias.CLINICAL_NOTES_MENU, fragment: FragmentNames.CLINICAL_NOTES_MENU })
class ClinicalNotesMenu extends BaseController {

  cnService = new ClinicalNotesService(this);

  evolutionId!: string;
  encounterId!: number;
  patientId!: number;
  clinicalNoteTypeId!: string;

  @Inject(Alias.NEW_CLINICAL_NOTE_ACTION)
  private newClinicalNoteAction!: ActionAPI;

  @Inject(Alias.DELETE_CLINICAL_NOTE_ACTION)
  private deleteClinicalNoteAction!: ActionAPI;

  @Inject(Alias.INACTIVATE_CLINICAL_NOTE_ACTION)
  private inactivateClinicalNoteAction!: ActionAPI;

  @Inject(Alias.ADD_MY_STANDARD_TEXT_ACTION)
  private addMyStandardTextAction!: ActionAPI;

  @Inject(Alias.ADD_INSTITUTION_STANDARD_TEXT_ACTION)
  private addInstitutionStandardTextAction!: ActionAPI;

  @OnMount({ target: Alias.CLINICAL_NOTES_MENU })
  onMount(): void {
    const { encounterId, patientId, cdEvolucao } = this.historyService.getCurrentRouteParams();

    this.clinicalNoteTypeId = this.context.get('clinicalNoteTypeId') as string;

    this.encounterId = encounterId as number;
    this.patientId = patientId as number;
    this.evolutionId = cdEvolucao as string;
  }

  configureMenu(): void {
    const { encounterId, patientId, cdEvolucao, eStatus } = this.historyService.getCurrentRouteParams();

    this.clinicalNoteTypeId = this.context.get('clinicalNoteTypeId') as string;
    this.encounterId = encounterId as number;
    this.patientId = patientId as number;
    this.evolutionId = cdEvolucao as string;

    const routeGetUrl = this.historyService.getCurrentRoute().split('/');

    if (eStatus == 'A') {
      this.deleteClinicalNoteAction.setEnabled(false);
      this.inactivateClinicalNoteAction.setEnabled(true);
      this.addMyStandardTextAction.setEnabled(false);
      this.addInstitutionStandardTextAction.setEnabled(false);
    } else if (eStatus == 'P') {
      this.inactivateClinicalNoteAction.setEnabled(false);
      this.deleteClinicalNoteAction.setEnabled(true);
      this.addMyStandardTextAction.setEnabled(true);
      this.addInstitutionStandardTextAction.setEnabled(true);
    } else if (routeGetUrl[9] == 'create') {
      this.newClinicalNoteAction.setEnabled(false);
      this.deleteClinicalNoteAction.setEnabled(false);
      this.addMyStandardTextAction.setEnabled(true);
      this.addInstitutionStandardTextAction.setEnabled(true);
      this.inactivateClinicalNoteAction.setEnabled(false);
    } else {
      this.inactivateClinicalNoteAction.setEnabled(false);
      this.deleteClinicalNoteAction.setEnabled(true);
    }
  }

  @OnMount([ Alias.NEW_CLINICAL_NOTE_ACTION, Alias.DELETE_CLINICAL_NOTE_ACTION, Alias.INACTIVATE_CLINICAL_NOTE_ACTION, Alias.ADD_MY_STANDARD_TEXT_ACTION, Alias.ADD_INSTITUTION_STANDARD_TEXT_ACTION ])
  handleMountonClinicalNoteActions({ target }: EventData<ActionAPI>): void {
    const isDeceased = SuepService.isDeceased;
    target.setEnabled(!isDeceased);

    if (!isDeceased) {
      this.configureMenu();
    }
  }

  @OnClick({ target: Alias.DELETE_CLINICAL_NOTE_ACTION })
  onDelete(): void {

    const evolutionId = this.evolutionId;
    const encounterId = this.encounterId;
    const patientId = this.patientId;
    const cnService = new ClinicalNotesService(this);

    cnService.confirmDelete(patientId, encounterId, parseInt(evolutionId));
  }

  @OnClick({ target: Alias.INACTIVATE_CLINICAL_NOTE_ACTION })
  onInactivate(): void {

    this.context.set({
      cdEvolucao: this.evolutionId,
      cdPessoaFisica: this.patientId,
      nrAtendimento: this.encounterId,
    });

    const evolutionId = this.evolutionId;
    const patientId = this.patientId;
    const encounterId = this.encounterId;

    const modalProperties: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'patient-evolution-inactivation',
      id: 'patient-evolution-inactivation',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'title.disable-record',
      uuid: generateUUID(),
    };

    modalProperties.contextData = {
      encounterId,
      evolutionId,
      patientId,
    }

    ModalService.openDefault(modalProperties);
  }

  @OnClick({ target: Alias.NEW_CLINICAL_NOTE_ACTION })
  onNew(): void {
    applicationContext.redirect(ApplicationRoute.CLINICAL_NOTES_CREATE);
  }

  @OnClick({ target: Alias.ADD_MY_STANDARD_TEXT_ACTION })
  onMyStandardTextClick(): void {
    const clinicalNoteTypeId = this.context.get('clinicalNoteTypeId') as string;

    const modalProperties: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      contextData: { clinicalNoteTypeId },
      focusOnOpen: false,
      fragment: 'clinical-notes-my-standard-text',
      id: 'clinical-notes-my-standard-text',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'form-title.clinical-notes-edit.standard-text',
      type: ModalType.FULLSCREEN,
      uuid: generateUUID(),
    };

    ModalService.openDefault(modalProperties);
  }

  @OnClick({ target: Alias.ADD_INSTITUTION_STANDARD_TEXT_ACTION })
  onInstStandardTextClick(): void {
    const clinicalNoteTypeId = this.context.get('clinicalNoteTypeId') as string;

    const modalProperties: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      contextData: { clinicalNoteTypeId },
      focusOnOpen: false,
      fragment: 'clinical-notes-institution-standard-text',
      id: 'clinical-notes-institution-standard-text',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'form-title.clinical-notes-edit.standard-text',
      uuid: generateUUID(),
    };

    ModalService.openDefault(modalProperties);
  }
}

export default ClinicalNotesMenu
